/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, ChevronLeft, Flame, Award, Star, Users, Hexagon, Gift, Calendar, Trophy } from 'lucide-react';
import axios from '../../utils/api';
import { API } from '../../utils/api';
import { useCommunityAuth } from '../../context/CommunityAuthContext';

/**
 * Reusable bundle of the three community impact sections that the product
 * wants to show on both the Impact page and the ME page:
 *   1) Your Impact (carousel)
 *   2) Your Stats (3-tile grid)
 *   3) Leaderboard
 *
 * Owns its own data fetch + the supporting Streak/Badges modals so callers
 * can drop it in without any extra wiring.
 */

const COMM = `${API}/community`;
const TEAL_ACCENT = '#5DBFAD';
const TEAL_LIGHT = '#E8F5F3';
const ORANGE_ACCENT = '#F8AD7B';
const MISSED_COLOR = '#EF4444';

const getBadgeIcon = (type, size = 24) => {
  const icons = {
    first_action: { icon: Star, color: '#F5A623', bg: 'rgba(245,166,35,0.12)' },
    streak_7: { icon: Flame, color: '#E8795E', bg: 'rgba(232,121,94,0.12)' },
    helper: { icon: Users, color: TEAL_ACCENT, bg: TEAL_LIGHT },
    leader: { icon: Trophy, color: '#F5A623', bg: 'rgba(245,166,35,0.12)' },
    default: { icon: Award, color: TEAL_ACCENT, bg: TEAL_LIGHT },
  };
  const badge = icons[type] || icons.default;
  const I = badge.icon;
  return (
    <div style={{ width: size + 16, height: size + 16, borderRadius: 12, background: badge.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <I size={size} color={badge.color} />
    </div>
  );
};

export default function CommunityImpactSections() {
  const navigate = useNavigate();
  const { user, token } = useCommunityAuth();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const [scoreData, setScoreData] = useState(null);
  const [impactStats, setImpactStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [impactCarouselIndex, setImpactCarouselIndex] = useState(0);
  const [showAllLeaderboard, setShowAllLeaderboard] = useState(false);
  const [showStreakModal, setShowStreakModal] = useState(false);
  const [showBadgesModal, setShowBadgesModal] = useState(false);
  const [weekStreakData, setWeekStreakData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [scoreRes, impactRes, lbRes] = await Promise.all([
          axios.get(`${COMM}/social-score/me`, authHeaders),
          axios.get(`${COMM}/impact/stats`, authHeaders),
          axios.get(`${COMM}/leaderboard`, authHeaders),
        ]);
        if (cancelled) return;
        setScoreData(scoreRes.data);
        setImpactStats(impactRes.data);
        setLeaderboard(lbRes.data);
      } catch {
        /* silently fail — calling page may handle its own error states */
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!showStreakModal) return;
    (async () => {
      try {
        const r = await axios.get(`${COMM}/streak/week`, authHeaders);
        setWeekStreakData(r.data);
      } catch { /* ignore */ }
    })();
  }, [showStreakModal]);

  if (!scoreData || !impactStats) return null;

  const impactTiles = [
    { icon: Calendar, value: impactStats.events_joined || 0, label: 'Events Joined', description: 'Total events you participated in', borderColor: TEAL_ACCENT },
    { icon: Users, value: impactStats.orgs_connected || 0, label: 'Organisations', description: 'Connected with unique orgs', borderColor: '#8B5CF6' },
    { icon: Gift, value: impactStats.stars_redeemed || 0, label: 'Stars Redeemed', description: 'Stars used for rewards', borderColor: '#F59E0B' },
  ];

  return (
    <>
      {/* Your Impact — Horizontal Carousel */}
      <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 22, background: 'rgba(242,140,40,0.06)', padding: 12, marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1F2937', marginBottom: 12 }}>Your Impact</h3>
        {impactTiles.length > 0 && (
          <div style={{ position: 'relative' }}>
            <div style={{ background: '#FFFFFF', borderRadius: 16, padding: 24, border: `3px solid ${impactTiles[impactCarouselIndex].borderColor}`, minHeight: 160, transition: 'border-color 0.3s ease' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <div style={{ width: 64, height: 64, borderRadius: 16, border: `2px solid ${impactTiles[impactCarouselIndex].borderColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  {React.createElement(impactTiles[impactCarouselIndex].icon, { size: 28, color: impactTiles[impactCarouselIndex].borderColor, strokeWidth: 1.5 })}
                </div>
                <div style={{ fontSize: 32, fontWeight: 700, color: '#1F2937', marginBottom: 4 }}>{impactTiles[impactCarouselIndex].value}</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{impactTiles[impactCarouselIndex].label}</div>
                <div style={{ fontSize: 13, color: '#6B7280' }}>{impactTiles[impactCarouselIndex].description}</div>
              </div>
            </div>
            <button onClick={() => setImpactCarouselIndex(p => p === 0 ? impactTiles.length - 1 : p - 1)} style={{ position: 'absolute', left: -12, top: '50%', transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', background: '#FFF', border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <ChevronLeft size={20} color="#6B7280" />
            </button>
            <button onClick={() => setImpactCarouselIndex(p => p === impactTiles.length - 1 ? 0 : p + 1)} style={{ position: 'absolute', right: -12, top: '50%', transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', background: '#FFF', border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <ChevronRight size={20} color="#6B7280" />
            </button>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 12 }}>
              {impactTiles.map((tile, i) => <button key={i} onClick={() => setImpactCarouselIndex(i)} style={{ width: i === impactCarouselIndex ? 20 : 8, height: 8, borderRadius: 4, background: i === impactCarouselIndex ? tile.borderColor : '#D1D5DB', border: 'none', cursor: 'pointer', transition: 'all 0.2s ease' }} />)}
            </div>
          </div>
        )}
      </div>

      {/* Your Stats */}
      <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 22, background: 'rgba(231,111,81,0.05)', padding: 12, marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1F2937', marginBottom: 12 }}>Your Stats</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {[
            { icon: Hexagon, value: scoreData.active_mad_count || 0, label: 'My MAD', onClick: () => navigate('/community/profile?tab=mymad') },
            { icon: Flame, value: user?.streak_count || 0, label: 'Streak', onClick: () => setShowStreakModal(true) },
            { icon: Award, value: scoreData.achievements?.length || 0, label: 'Badges', onClick: () => setShowBadgesModal(true) },
          ].map((stat, i) => (
            <div key={i} onClick={stat.onClick} style={{ background: '#FFFFFF', borderRadius: 12, padding: '16px 8px', textAlign: 'center', cursor: 'pointer', border: '1.5px solid rgba(231,111,81,0.30)', boxShadow: '0 0 0 3px rgba(231,111,81,0.07)' }}>
              <stat.icon size={20} color={TEAL_ACCENT} strokeWidth={1.5} style={{ marginBottom: 8 }} />
              <div style={{ fontSize: 18, fontWeight: 700, color: '#1F2937' }}>{stat.value}</div>
              <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Leaderboard */}
      <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 22, background: 'rgba(77,182,172,0.05)', padding: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1F2937', margin: 0 }}>Leaderboard</h3>
        </div>
        <div style={{ background: '#FFFFFF', borderRadius: 12, overflow: 'hidden', border: '1.5px solid rgba(77,182,172,0.30)' }}>
          {(showAllLeaderboard ? leaderboard : leaderboard.slice(0, 5)).map((entry, i) => (
            <div key={entry.rank || i} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: i < (showAllLeaderboard ? leaderboard.length : 5) - 1 ? '1px solid #F3F4F6' : 'none', background: entry.is_me ? TEAL_LIGHT : 'transparent' }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: entry.rank <= 3 ? entry.rank === 1 ? '#FEF3C7' : entry.rank === 2 ? '#F3F4F6' : '#FED7AA' : '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: entry.rank <= 3 ? 16 : 13, fontWeight: 600, color: '#374151', marginRight: 12 }}>
                {entry.rank === 1 ? '\uD83E\uDD47' : entry.rank === 2 ? '\uD83E\uDD48' : entry.rank === 3 ? '\uD83E\uDD49' : entry.rank}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>
                  {entry.display_name}
                  {entry.is_me && <span style={{ marginLeft: 6, fontSize: 11, color: TEAL_ACCENT }}>(You)</span>}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14, fontWeight: 600, color: '#F59E0B' }}>
                <Star size={14} fill="#F59E0B" color="#F59E0B" />
                {entry.stars || 0}
              </div>
            </div>
          ))}
          {leaderboard.length > 5 && (
            <button onClick={() => setShowAllLeaderboard(!showAllLeaderboard)} style={{ width: '100%', padding: 12, background: '#F9FAFB', border: 'none', borderTop: '1px solid #E5E7EB', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: TEAL_ACCENT }}>
              {showAllLeaderboard ? 'View Less' : `View More (${leaderboard.length - 5} more)`}
              <ChevronDown size={16} style={{ transform: showAllLeaderboard ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
            </button>
          )}
        </div>
      </div>

      {/* Streak modal — shown from "Streak" tile */}
      {showStreakModal && (
        <div onClick={() => setShowStreakModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#FFF', borderRadius: 16, padding: 24, width: '100%', maxWidth: 340 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <button onClick={() => setShowStreakModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ width: 72, height: 72, borderRadius: '50%', background: `linear-gradient(135deg, ${TEAL_LIGHT} 0%, rgba(93,191,173,0.2) 100%)`, border: `2px solid ${TEAL_ACCENT}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <Flame size={32} color={TEAL_ACCENT} strokeWidth={1.5} />
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#1F2937', marginBottom: 4 }}>{weekStreakData?.current_streak || user?.streak_count || 0} Day Streak!</div>
              <div style={{ fontSize: 14, color: '#6B7280' }}>{(weekStreakData?.current_streak || 0) > 0 ? "You're on the right track" : "Start logging to build your streak"}</div>
            </div>
            <div style={{ background: '#F9FAFB', borderRadius: 12, padding: 16, marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>This Week</div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                {(weekStreakData?.week_data || []).map((d, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: d.isToday ? TEAL_ACCENT : '#9CA3AF', marginBottom: 6, fontWeight: d.isToday ? 600 : 400 }}>{d.day}</div>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: d.isCompleted ? TEAL_ACCENT : d.isMissed ? MISSED_COLOR : d.isFuture ? '#F3F4F6' : 'transparent', border: `2px solid ${d.isCompleted ? TEAL_ACCENT : d.isMissed ? MISSED_COLOR : d.isToday ? TEAL_ACCENT : '#E5E7EB'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: d.isFuture ? 0.5 : 1 }}>
                      {d.isCompleted && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFF" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
                      {d.isMissed && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FFF" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12" /></svg>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ background: '#F9FAFB', borderRadius: 10, padding: '14px 12px', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: TEAL_ACCENT }}>{weekStreakData?.current_streak || 0}</div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>Current Streak</div>
              </div>
              <div style={{ background: '#F9FAFB', borderRadius: 10, padding: '14px 12px', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: ORANGE_ACCENT }}>{weekStreakData?.longest_streak || 0}</div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>Longest Streak</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Badges modal — shown from "Badges" tile */}
      {showBadgesModal && (
        <div onClick={() => setShowBadgesModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#FFF', borderRadius: 16, padding: 24, width: '100%', maxWidth: 340, maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, color: '#1F2937', margin: 0 }}>Badges Earned</h3>
              <button onClick={() => setShowBadgesModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>
            {(scoreData.achievements?.length || 0) > 0 ? scoreData.achievements.map(a => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: '#F8FAFB', borderRadius: 12, border: '1px solid #E5E7EB', marginBottom: 8 }}>
                {getBadgeIcon(a.badge_type || 'default', 24)}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>{a.title}</div>
                  {a.description && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{a.description}</div>}
                </div>
              </div>
            )) : (
              <div style={{ textAlign: 'center', padding: '32px 16px', color: '#6B7280' }}>
                <Award size={40} color="#E5E7EB" style={{ marginBottom: 12 }} />
                <div style={{ fontSize: 14 }}>No badges earned yet</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>Complete actions to earn badges!</div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
